// Everything the gym owns, as files they can open anywhere.
//
// The competitive teardown found that most incumbents either charge to export
// your own data or make you ask support for it. Free self-service export is the
// reason to switch, so it exports the whole working set, not a summary: names,
// contact details, plans, balances, every booking with its fee and status, and
// every invoice with its dunning state.
//
// A pure function of the snapshot, so it is testable without a browser and
// works identically against demo data and a live org.

import { csvFile } from '../lib/csv.js';
import { buildSchedule } from '../lib/recurrence.js';

const money = (cents) => (cents === null || cents === undefined
  ? '' : (cents / 100).toFixed(2));

export function buildExport(state, { todayKey } = {}) {
  const planName = (id) => state.plans.find((p) => p.id === id)?.name ?? id ?? '';
  const memberName = (id) => state.members.find((m) => m.id === id)?.name ?? id ?? '';
  const staffName = (id) => state.staff.find((s) => s.id === id)?.name ?? '';
  const roomName = (id) => (state.rooms || []).find((r) => r.id === id)?.name ?? '';

  // Bookings reference occurrences, which are computed rather than stored. An
  // export that emitted "tpl_wod_am:2026-08-25" would be unreadable to the
  // human it is for, so the class is resolved to its name, date and time.
  const occurrences = new Map();
  for (const b of state.bookings) {
    if (!b.occurrenceId || occurrences.has(b.occurrenceId)) continue;
    const [templateId, date] = [
      b.occurrenceId.slice(0, b.occurrenceId.lastIndexOf(':')),
      b.occurrenceId.slice(b.occurrenceId.lastIndexOf(':') + 1),
    ];
    const template = state.templates.find((t) => t.id === templateId);
    const [occ] = template ? buildSchedule([template], date, date) : [];
    occurrences.set(b.occurrenceId, occ || { name: templateId, date, time: '' });
  }
  const occ = (id) => occurrences.get(id) || {};

  const files = [
    {
      name: 'members.csv',
      label: 'Members',
      rows: state.members,
      columns: [
        { key: 'id', header: 'member_id' },
        { key: 'name', header: 'name' },
        { key: 'email', header: 'email' },
        { key: 'phone', header: 'phone' },
        { key: 'status', header: 'status' },
        { key: 'joinedOn', header: 'joined_on' },
        { header: 'waiver_signed_at', value: (m) => m.waiverSignedAt ?? '' },
        { header: 'emergency_contact_name', value: (m) => m.emergencyContact?.name ?? '' },
        { header: 'emergency_contact_phone', value: (m) => m.emergencyContact?.phone ?? '' },
        { key: 'notes', header: 'notes' },
      ],
    },
    {
      name: 'memberships.csv',
      label: 'Memberships',
      rows: state.memberships,
      columns: [
        { key: 'id', header: 'membership_id' },
        { key: 'memberId', header: 'member_id' },
        { header: 'member_name', value: (s) => memberName(s.memberId) },
        { header: 'plan', value: (s) => planName(s.planId) },
        { key: 'status', header: 'status' },
        { key: 'startDate', header: 'start_date' },
        { header: 'end_date', value: (s) => s.endDate ?? '' },
        { key: 'currentPeriodStart', header: 'period_start' },
        { key: 'currentPeriodEnd', header: 'period_end' },
        { key: 'interval', header: 'interval' },
        { header: 'amount', value: (s) => money(s.amountCents) },
        { header: 'credits_remaining', value: (s) => s.creditsRemaining ?? 0 },
        { header: 'credits_expire_on', value: (s) => s.creditsExpireOn ?? '' },
        { header: 'past_due', value: (s) => money(s.pastDueCents ?? 0) },
        // Freezes are a nested array; one row per membership stays importable,
        // so they are flattened to a readable list rather than dropped.
        {
          header: 'holds',
          value: (s) => (s.freezes || []).map((f) => `${f.from}..${f.to || 'open'}`).join(' '),
        },
      ],
    },
    {
      name: 'bookings.csv',
      label: 'Bookings',
      rows: state.bookings,
      columns: [
        { key: 'id', header: 'booking_id' },
        { header: 'member_name', value: (b) => memberName(b.memberId) },
        { key: 'memberId', header: 'member_id' },
        { header: 'class', value: (b) => occ(b.occurrenceId).name ?? '' },
        { header: 'date', value: (b) => occ(b.occurrenceId).date ?? '' },
        { header: 'time', value: (b) => occ(b.occurrenceId).time ?? '' },
        { header: 'coach', value: (b) => staffName(occ(b.occurrenceId).staffId) },
        { header: 'room', value: (b) => roomName(occ(b.occurrenceId).roomId) },
        { key: 'status', header: 'status' },
        { header: 'waitlist_position', value: (b) => b.waitlistPosition ?? '' },
        { header: 'consumed_credit', value: (b) => Boolean(b.consumedCredit) },
        { header: 'consumed_usage', value: (b) => Boolean(b.consumedUsage) },
        { header: 'fee', value: (b) => money(b.feeCents ?? 0) },
        { key: 'bookedAt', header: 'booked_at' },
        { header: 'checked_in_at', value: (b) => b.checkedInAt ?? '' },
        { header: 'cancelled_at', value: (b) => b.cancelledAt ?? '' },
      ],
    },
    {
      name: 'invoices.csv',
      label: 'Invoices',
      rows: state.invoices,
      columns: [
        { key: 'id', header: 'invoice_id' },
        { header: 'member_name', value: (i) => memberName(i.memberId) },
        { key: 'memberId', header: 'member_id' },
        { key: 'status', header: 'status' },
        { header: 'total', value: (i) => money(i.totalCents) },
        { header: 'currency', value: (i) => i.currency ?? state.org.currency ?? 'USD' },
        { key: 'issuedOn', header: 'issued_on' },
        { header: 'paid_on', value: (i) => i.paidOn ?? '' },
        { header: 'failed_on', value: (i) => i.failedOnKey ?? '' },
        { header: 'attempts_made', value: (i) => i.attemptsMade ?? 0 },
      ],
    },
    {
      name: 'plans.csv',
      label: 'Plans',
      rows: state.plans,
      columns: [
        { key: 'id', header: 'plan_id' },
        { key: 'name', header: 'name' },
        { key: 'kind', header: 'kind' },
        { header: 'price', value: (p) => money(p.amountCents) },
        { key: 'interval', header: 'interval' },
        { header: 'usage_limit', value: (p) => p.limit ?? '' },
        { header: 'limit_period', value: (p) => p.limitPeriod ?? '' },
        { header: 'credits', value: (p) => p.credits ?? '' },
        { header: 'expiry_days', value: (p) => p.expiryDays ?? '' },
        { header: 'programs', value: (p) => (p.programs || []).join(' ') },
        { header: 'blocks_when_past_due', value: (p) => Boolean(p.blockWhenPastDue) },
        { header: 'active', value: (p) => Boolean(p.active) },
      ],
    },
    {
      name: 'classes.csv',
      label: 'Class schedule',
      rows: state.templates,
      columns: [
        { key: 'id', header: 'class_id' },
        { key: 'name', header: 'name' },
        { key: 'program', header: 'program' },
        { header: 'weekdays', value: (t) => (t.weekdays || []).join(' ') },
        { key: 'time', header: 'start_time' },
        { key: 'durationMin', header: 'duration_min' },
        { key: 'capacity', header: 'capacity' },
        { header: 'coach', value: (t) => staffName(t.staffId) },
        { header: 'room', value: (t) => roomName(t.roomId) },
        { key: 'timeZone', header: 'time_zone' },
        { key: 'startDate', header: 'start_date' },
        { header: 'end_date', value: (t) => t.endDate ?? '' },
      ],
    },
  ];

  const stamp = todayKey || new Date().toISOString().slice(0, 10);
  const slug = (state.org.name || 'floatdesk')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

  return files.map((f) => ({
    label: f.label,
    count: f.rows.length,
    filename: `${slug}-${f.name.replace('.csv', '')}-${stamp}.csv`,
    content: csvFile(f.rows, f.columns),
  }));
}
